import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
	Box,
	CircularProgress,
	GlobalStyles,
	Paper,
	Typography,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	ColGroupDef,
	GridApi,
	GridReadyEvent,
	RowClassParams,
	ValueFormatterParams,
} from "ag-grid-community";
import { ErrorModal } from "@dexteel/mesf-core";
import moment from "moment";
import {
	isUsableDate,
	urlParam,
	useUrlState,
} from "@/shared/hooks/useUrlState";

import { KPIShiftReportToolbar } from "./KPIShiftReportToolbar";
import { useKPIShiftReportData } from "../hooks/useKPIShiftReport";
import { mesfColors } from "@/setup/ag-grid/mesf-theme";
import { isSummaryRow, type KPIShiftReportRow } from "../models/KPIShiftReport";

const CENTER_CELL = { textAlign: "center" } as const;
// The legacy stylesheet paints the Total columns #D0D0D0 and the summary row
// #88C0D9 (kpi-shift.css:29 and :35). Both are transplanted straight out of a 2013
// page and read as loud blocks next to everything else in this application, so the
// emphasis is kept and the colours come from the shared palette instead: a light
// tint for the aggregate columns, one step darker plus a rule above it for the
// summary row.
const TOTAL_CELL = {
	textAlign: "center",
	fontWeight: 600,
	backgroundColor: mesfColors.grey[50],
} as const;
const CENTER_HEADER = "ag-cell-center-header";
const HEADER_HEIGHT = 32;
const GROUP_HEADER_HEIGHT = 28;
const ROW_HEIGHT = 28;
const NO_ROWS_OVERLAY = `<span style="color:${mesfColors.text.secondary};font-size:13px">No shifts were produced in the selected range</span>`;
const SUMMARY_ROW = {
	backgroundColor: mesfColors.grey[100],
	fontWeight: 600,
	borderTop: `2px solid ${mesfColors.grey[300]}`,
} as const;

const decimals =
	(places: number) => (p: ValueFormatterParams<KPIShiftReportRow>) =>
		p.value === null || p.value === undefined
			? ""
			: Number(p.value).toFixed(places);

// The grid shows the same 13 columns as the legacy screen, in the same order and under the
// same two header groups. The procedure also returns CobbleEvents, CobbleBillets, CobbleRate
// and Availability, which the legacy grid does not display.
// A column that belongs to no group would otherwise render centred across both header
// rows, out of line with the children of TPH and Downtime Hours. Wrapping it in a
// titleless group puts its name on the same row as theirs.
const standalone = (
	col: ColDef<KPIShiftReportRow>,
): ColGroupDef<KPIShiftReportRow> => ({
	headerName: "",
	headerClass: CENTER_HEADER,
	children: [col],
});

// The grid shows the same 13 columns as the legacy screen, in the same order and under the
// same two header groups. The procedure also returns CobbleEvents, CobbleBillets, CobbleRate
// and Availability, which the legacy grid does not display.
const COLUMN_DEFS: (
	| ColDef<KPIShiftReportRow>
	| ColGroupDef<KPIShiftReportRow>
)[] = [
	standalone({
		field: "ProductionDate",
		headerName: "Production Date",
		headerTooltip: "Date based on shift schedule",
		width: 140,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p: ValueFormatterParams<KPIShiftReportRow>) =>
			p.data && isSummaryRow(p.data) ? "Summary" : (p.value ?? ""),
	}),
	standalone({
		field: "Shift",
		headerName: "Shift",
		width: 80,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	}),
	standalone({
		field: "Crew",
		headerName: "Crew",
		width: 80,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	}),
	{
		headerName: "TPH",
		headerClass: CENTER_HEADER,
		children: [
			{
				field: "ActualTPH",
				headerName: "Actual",
				headerTooltip: "weight of billets in Cooling Bed / Available Time",
				width: 90,
				sortable: true,
				cellStyle: CENTER_CELL,
				headerClass: CENTER_HEADER,
				valueFormatter: decimals(0),
			},
			{
				field: "TheoreticalTPH",
				headerName: "Theoretical",
				headerTooltip: "Theoretical TPH of the product mix",
				width: 110,
				cellStyle: CENTER_CELL,
				headerClass: CENTER_HEADER,
				valueFormatter: decimals(0),
			},
		],
	},
	standalone({
		field: "BilletsBetweenCobbles",
		headerName: "Billets between Cobbles",
		headerTooltip: "Billets out of furnace / (#cobbles+1)",
		width: 170,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: decimals(0),
	}),
	standalone({
		field: "SumDowntime",
		headerName: "Downtime %",
		headerTooltip: "All Delays / Available Time",
		width: 120,
		cellStyle: TOTAL_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: decimals(2),
	}),
	{
		headerName: "Downtime Hours",
		headerClass: CENTER_HEADER,
		children: [
			{
				field: "ActualChangeTimeH",
				headerName: "Change",
				headerTooltip: "Sum of Delays with category Change Product",
				width: 95,
				cellStyle: CENTER_CELL,
				headerClass: CENTER_HEADER,
				valueFormatter: decimals(1),
			},
			{
				field: "ElectricalDowntimeH",
				headerName: "Elec",
				headerTooltip: "Sum of Delays with category Electrical",
				width: 85,
				cellStyle: CENTER_CELL,
				headerClass: CENTER_HEADER,
				valueFormatter: decimals(1),
			},
			{
				field: "ProductionDowntimeH",
				headerName: "Prod",
				headerTooltip: "Sum of Delays with category Gap Time",
				width: 85,
				cellStyle: CENTER_CELL,
				headerClass: CENTER_HEADER,
				valueFormatter: decimals(1),
			},
			{
				field: "MechanicalDowntimeH",
				headerName: "Mech",
				headerTooltip: "Sum of Delays with category Mechanical",
				width: 85,
				cellStyle: CENTER_CELL,
				headerClass: CENTER_HEADER,
				valueFormatter: decimals(1),
			},
			{
				field: "CobbleDowntimeH",
				headerName: "Cobble",
				headerTooltip: "Sum of Delays with category cobble",
				width: 90,
				cellStyle: CENTER_CELL,
				headerClass: CENTER_HEADER,
				valueFormatter: decimals(1),
			},
			{
				field: "UncategorizedDowntimeH",
				headerName: "Uncat",
				headerTooltip: "Sum of Delays not declared",
				width: 90,
				cellStyle: CENTER_CELL,
				headerClass: CENTER_HEADER,
				valueFormatter: decimals(1),
			},
			{
				field: "DownDayDowntimeH",
				headerName: "Down Day",
				headerTooltip: "Sum of Down Day Delays",
				width: 110,
				cellStyle: CENTER_CELL,
				headerClass: CENTER_HEADER,
				valueFormatter: decimals(1),
			},
			{
				field: "SumDowntimeH",
				headerName: "Total",
				headerTooltip: "Sum of all Delays",
				flex: 1,
				minWidth: 90,
				cellStyle: TOTAL_CELL,
				headerClass: CENTER_HEADER,
				valueFormatter: decimals(1),
			},
		],
	},
];

const EXPORT_COLUMNS = [
	"ProductionDate",
	"Shift",
	"Crew",
	"ActualTPH",
	"TheoreticalTPH",
	"BilletsBetweenCobbles",
	"SumDowntime",
	"ActualChangeTimeH",
	"ElectricalDowntimeH",
	"ProductionDowntimeH",
	"MechanicalDowntimeH",
	"CobbleDowntimeH",
	"UncategorizedDowntimeH",
	"DownDayDowntimeH",
	"SumDowntimeH",
];

const URL_SCHEMA = {
	// Legacy default range (kpi-shift.js:174-175): start = today-7, end = today.
	start: urlParam.date(() => moment().startOf("day").subtract(7, "days")),
	end: urlParam.date(() => moment().startOf("day")),
	crewId: urlParam.string(),
};

export const KPIShiftReport = () => {
	const [{ start, end, crewId: crew }, setFilters] = useUrlState(URL_SCHEMA);
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<KPIShiftReportRow> | null>(null);

	const startStr = start.format("YYYY-MM-DD");
	const endStr = end.format("YYYY-MM-DD");

	const dataQuery = useKPIShiftReportData({
		start: startStr,
		end: endStr,
		crew,
	});

	// The procedure returns the totals row inside the same result set, marked by an empty
	// ProductionDate. Pinning it keeps it below the last shift row and, once the grid scrolls,
	// keeps it visible against the bottom edge instead of scrolling away — and it stays out of
	// the sort for free, the job the legacy did with its 2050-01-01 sentinel.
	//
	// An empty range still returns that one row, with every numeric column null, and the legacy
	// screen renders it. It is dropped here instead: a totals line under an empty grid says
	// nothing, and it contradicts the no-rows message the grid shows in its place.
	const { rows, summaryRows } = useMemo(() => {
		const all = dataQuery.data ?? [];
		const shiftRows = all.filter((row) => !isSummaryRow(row));
		return {
			rows: shiftRows,
			summaryRows: shiftRows.length ? all.filter(isSummaryRow) : [],
		};
	}, [dataQuery.data]);

	const handleGridReady = useCallback(
		(event: GridReadyEvent<KPIShiftReportRow>) => {
			gridApiRef.current = event.api;
		},
		[],
	);

	const handleStartChange = useCallback(
		(d: moment.Moment | null) => {
			if (isUsableDate(d)) setFilters({ start: d });
		},
		[setFilters],
	);

	const handleEndChange = useCallback(
		(d: moment.Moment | null) => {
			if (isUsableDate(d)) setFilters({ end: d });
		},
		[setFilters],
	);

	const handleRefresh = useCallback(() => {
		dataQuery.refetch();
	}, [dataQuery]);

	// The legacy export calls the procedure with @ExportToExcel = 1, whose branch returns the
	// shift rows without the totals row, so the workbook has no Summary line.
	const handleExportToExcel = useCallback(() => {
		if (!gridApiRef.current) return;
		gridApiRef.current.exportDataAsExcel({
			fileName: "KPI_Shift_Report.xlsx",
			columnKeys: EXPORT_COLUMNS,
			shouldRowBeSkipped: (params) =>
				!!params.node.data && isSummaryRow(params.node.data),
		});
	}, []);

	// The totals row has to sit directly under the last shift when everything fits, and stay
	// stuck to the bottom edge only once the grid actually scrolls. AG Grid pins it either way,
	// so the difference is the layout: autoHeight makes the grid end where the rows end, normal
	// gives it the full height and a scrollbar.
	const gridBoxRef = useRef<HTMLDivElement | null>(null);
	const [gridSpace, setGridSpace] = useState(0);

	useLayoutEffect(() => {
		const el = gridBoxRef.current;
		if (!el) return;
		const update = () => setGridSpace(el.clientHeight);
		update();
		const observer = new ResizeObserver(update);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const contentHeight =
		HEADER_HEIGHT +
		GROUP_HEADER_HEIGHT +
		(rows.length + summaryRows.length) * ROW_HEIGHT;
	const fitsWithoutScroll = gridSpace > 0 && contentHeight <= gridSpace;

	const getRowStyle = useCallback(
		(params: RowClassParams<KPIShiftReportRow>) =>
			params.node.rowPinned === "bottom" ? SUMMARY_ROW : undefined,
		[],
	);

	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				KPI Shift Report
			</Typography>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
					".ag-cell-center-header .ag-header-group-cell-label": {
						justifyContent: "center",
					},
					// In autoHeight AG Grid keeps a minimum height on the rows viewport so the
					// no-rows overlay has somewhere to sit. With rows on screen that reserve
					// becomes a gap between the last shift and the pinned totals row.
					".ag-layout-auto-height .ag-center-cols-clipper, .ag-layout-auto-height .ag-center-cols-viewport":
						{
							minHeight: 0,
						},
				}}
			/>
			<Paper elevation={3}>
				<KPIShiftReportToolbar
					start={start}
					end={end}
					onStartChange={handleStartChange}
					onEndChange={handleEndChange}
					crew={crew}
					onCrewChange={(value) => setFilters({ crewId: value })}
					onRefresh={handleRefresh}
					onExportExcel={handleExportToExcel}
					isLoading={dataQuery.isFetching}
				/>
			</Paper>

			<Paper elevation={3} sx={{ flex: 1, overflow: "hidden" }}>
				<Box ref={gridBoxRef} sx={{ height: "100%" }}>
					{dataQuery.isLoading ? (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								height: "100%",
							}}
						>
							<CircularProgress />
						</Box>
					) : (
						<AgGridReact<KPIShiftReportRow>
							columnDefs={COLUMN_DEFS}
							rowData={rows}
							pinnedBottomRowData={summaryRows}
							getRowStyle={getRowStyle}
							rowModelType="clientSide"
							domLayout={
								rows.length > 0 && fitsWithoutScroll ? "autoHeight" : "normal"
							}
							pagination={false}
							defaultColDef={{ resizable: true }}
							autoSizeStrategy={{ type: "fitGridWidth" }}
							suppressContextMenu
							overlayNoRowsTemplate={NO_ROWS_OVERLAY}
							headerHeight={32}
							groupHeaderHeight={28}
							rowHeight={28}
							onGridReady={handleGridReady}
						/>
					)}
				</Box>
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
